import { Directive, ElementRef, HostListener } from '@angular/core';

/**
 * Disclosure-style sub menu for the main nav. Applied to the <li> that wraps a
 * toggle <button class="has-submenu"> together with its sibling <ul> of links, so
 * that focus and pointer events for both are handled in one place.
 *
 * The main nav is deliberately NOT an ARIA menubar. That pattern mandates a single
 * tab stop plus complete arrow-key handling, which is what the previous
 * nav-sub-menu-header/item/parent directives attempted via MainNavService; any gap
 * in that chain left items with no reachable focus path at all. As plain links in a
 * disclosure, the native tab order reaches every item for free.
 *
 * Expansion is tracked here rather than with a CSS :focus-within rule so that
 * aria-expanded on the toggle button always matches what is actually visible.
 */
@Directive({
  selector: '[appNavSubMenu]',
  standalone: true,
  exportAs: 'appNavSubMenu'
})
export class NavSubMenuDirective {
  public expanded: boolean = false;

  constructor(private elRef: ElementRef<HTMLElement>) { }

  /**
   * Enter and Space on the toggle button both arrive here as a click event, so a
   * plain toggle covers keyboard, mouse and touch without branching on viewport
   * width or hover capability the way the previous isClickOnly check did.
   */
  @HostListener('click', ['$event'])
  public onToggleClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.has-submenu')) {
      this.expanded = !this.expanded;
    }
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    this.expanded = true;
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    //Never close underneath a keyboard user who is still inside the sub menu.
    if (!this.elRef.nativeElement.contains(document.activeElement)) {
      this.expanded = false;
    }
  }

  /**
   * Tabbing (or clicking) past the sub menu closes it. relatedTarget is where focus
   * is heading, so focus moving between the button and its own links is ignored.
   */
  @HostListener('focusout', ['$event'])
  public onFocusOut(event: FocusEvent): void {
    if (!this.elRef.nativeElement.contains(event.relatedTarget as Node)) {
      this.expanded = false;
    }
  }

  /**
   * WCAG 2.1 SC 1.4.13 - dismissable without moving focus. Focus returns to the
   * toggle so it does not get stranded on a link that is no longer visible.
   */
  @HostListener('keydown.esc')
  public onEscape(): void {
    if (!this.expanded) {
      return;
    }
    this.expanded = false;
    this.elRef.nativeElement.querySelector<HTMLElement>('.has-submenu')?.focus();
  }
}
